import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useFormContext, useWatch } from 'react-hook-form';
import { Alert, Collapse } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { StickyFormButtons } from '../../../../../components/Form/StickyFormButtons';
import { useInvalidateKPIRequests } from '../../KPIRequests/KPIRequestResponse/hooks/useInvalidateKPIRequests';
import { ManageCompanyFieldsTypes } from '../../../../../schemas/template-assignment-schema';
import { clientConfigFieldsAtom } from '../../../../../services/state/AppConfigStateJ';
import { MaggieFeatureFlags } from '../../../../../util/feature-flags';
import { useApplyRespondentSuggestions } from '../../../../../services/queries-hooks/RespondentApprovalStatusQueriesHooks';
import { diffNewEmails } from '../../../../../util/respondentStatus';
import { getErrorMessage } from '../../../../../services/queryHelpers';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import { useCompanyOnboardedStatus } from '../../../hooks/useRespondentApprovalsEnabled';
import { useAllKpiCompaniesActions } from './useAllKpiCompaniesActions';
import { useManageCompanyFormMethods } from './useManageCompanyFormMethods';
import { useManageCompanySubmit } from './useManageCompanySubmit';
import { useRespondentChangeWarning } from './useRespondentChangeWarning';
import { ManageSingleCompanyFields } from './ManageSingleCompanyFields';

export interface IManageCompanyFormProps {
  defaultValues: ManageCompanyFieldsTypes;
  companyId: number;
}
export function ManageSingleCompanyForm({ defaultValues, companyId }: IManageCompanyFormProps) {
  const [loading, setLoading] = useState(false);
  const { resetUIState: handleCloseModal } = useAllKpiCompaniesActions();
  const invalidateKpiRequests = useInvalidateKPIRequests();

  const isConsolidated =
    (useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection as boolean) ?? false;
  const { useStarbreakerCollectionV2 } = useFlags<MaggieFeatureFlags>();
  const isConsolidatedV2 = isConsolidated && useStarbreakerCollectionV2;
  const { isOnboarded } = useCompanyOnboardedStatus(companyId);

  const methods = useManageCompanyFormMethods(defaultValues!, 'manage', {
    validateDomain: isConsolidatedV2,
  });

  const submit = useManageCompanySubmit({
    companyId,
    defaultValues: defaultValues!,
    isConsolidatedV2,
    isOnboarded,
  });
  const applyRespondentSuggestions = useApplyRespondentSuggestions();
  const { pushErrorToast } = useToastMessageState();

  const handleSubmit = useCallback(
    async (data: ManageCompanyFieldsTypes) => {
      setLoading(true);
      try {
        const ok = await submit(data);
        if (!ok) {
          return;
        }
        // Onboarded respondents are persisted out-of-band via the dedicated endpoints (they were
        // omitted from the PUT above); non-onboarded respondents rode the PUT.
        if (isOnboarded) {
          const prev = (defaultValues?.respondents ?? []).filter((e): e is string => !!e);
          const next = (data.respondents ?? []).filter((e): e is string => !!e);
          const change = { added: diffNewEmails(next, prev), removed: diffNewEmails(prev, next) };
          if (change.added.length > 0 || change.removed.length > 0) {
            try {
              await applyRespondentSuggestions(companyId, change);
            } catch (err) {
              pushErrorToast({
                message: getErrorMessage(err, 'Respondent update failed to save. Please try again.'),
              });
              return;
            }
          }
        }
        invalidateKpiRequests();
        handleCloseModal();
      } finally {
        setLoading(false);
      }
    },
    [
      submit,
      isOnboarded,
      applyRespondentSuggestions,
      invalidateKpiRequests,
      handleCloseModal,
      pushErrorToast,
      companyId,
      defaultValues,
    ]
  );

  return (
    <FormProvider {...methods}>
      <ManageSingleCompanyFields companyId={companyId} />
      <RespondentWarning companyId={companyId} />
      <StickyFormButtons
        onCancel={handleCloseModal}
        onSubmit={methods.handleSubmit(handleSubmit)}
        loading={loading}
        style={{ padding: '0.5rem 0 2rem' }}
        submitLabel='Save Changes'
      />
    </FormProvider>
  );
}

function RespondentWarning({ companyId }: { companyId: number }) {
  const {
    control,
    formState: { defaultValues },
  } = useFormContext<ManageCompanyFieldsTypes>();
  const respondents = useWatch({ control: control, name: 'respondents' }) as string[] | null;
  const secondaryRespondents = useWatch({ control: control, name: 'secondaryRespondents' }) as
    | string[]
    | null;
  const initialRespondents = useMemo(
    () =>
      new Set(
        (defaultValues?.respondents ?? []).concat(defaultValues?.secondaryRespondents ?? [])
      ) as Set<string>,
    [defaultValues]
  );

  const allRespondents = useMemo(
    () => (respondents ?? []).concat(secondaryRespondents ?? []),
    [respondents, secondaryRespondents]
  );

  const warning = useRespondentChangeWarning({
    companyId,
    respondents: allRespondents,
    initialRespondents,
  });
  return (
    <Collapse in={!!warning}>
      <Alert severity='warning' style={{ marginBottom: '1rem' }}>
        {warning}
      </Alert>
    </Collapse>
  );
}
